import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import BackButton from '@/components/BackButton';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  TreeStructure,
  Crown,
  Briefcase,
  Money,
  User as UserIcon,
  CaretDown,
  CaretRight,
  MagnifyingGlass,
  UsersThree,
} from '@phosphor-icons/react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';
const LOGO_URL = '/branding/logo.png';

const ROLE_THEME = {
  gm: { bg: 'bg-amber-50', border: 'border-amber-300', text: 'text-amber-800', icon: Crown, ar: 'مدير عام', en: 'GM' },
  dept_manager: { bg: 'bg-blue-50', border: 'border-blue-300', text: 'text-blue-800', icon: Briefcase, ar: 'مدير قسم', en: 'Mgr' },
  finance: { bg: 'bg-emerald-50', border: 'border-emerald-300', text: 'text-emerald-800', icon: Money, ar: 'مدير مالي', en: 'Finance' },
  employee: { bg: 'bg-violet-50', border: 'border-violet-300', text: 'text-violet-800', icon: UserIcon, ar: 'موظف', en: 'Employee' },
};

function nodeMatchesSearch(node, q) {
  if (!q) return true;
  const parts = [node.name, node.name_en, node.position, node.position_en, node.department, node.email];
  const haystack = parts.filter(Boolean).join(' ').toLowerCase();
  if (haystack.indexOf(q) !== -1) return true;
  const kids = node.children || [];
  for (let i = 0; i < kids.length; i++) {
    if (nodeMatchesSearch(kids[i], q)) return true;
  }
  return false;
}

function TreeNode(props) {
  const node = props.node;
  const depth = props.depth;
  const isAr = props.isAr;
  const search = props.search;
  const [open, setOpen] = useState(true);
  const theme = ROLE_THEME[node.role] || ROLE_THEME.employee;
  const Icon = theme.icon;
  const children = node.children || [];
  const hasKids = children.length > 0;

  if (search && !nodeMatchesSearch(node, search)) return null;

  let directHit = true;
  if (search) {
    const parts = [node.name, node.name_en, node.position, node.department, node.email];
    const hay = parts.filter(Boolean).join(' ').toLowerCase();
    directHit = hay.indexOf(search) !== -1;
  }

  const rowPad = depth > 0 ? (isAr ? 'pe-5' : 'ps-5') : '';
  const rowClass = 'flex items-start gap-2 ' + rowPad;
  const childIndent = isAr ? 'pe-6 border-r-2' : 'ps-6 border-l-2';
  const childWrap = 'mt-2 space-y-2 border-dashed border-violet-200 ' + childIndent;
  const ring = directHit && search ? ' ring-2 ring-offset-1 ring-violet-300' : '';
  const cardClass = 'flex-1 rounded-lg border-2 p-3 transition-all hover:shadow-md ' + theme.border + ' ' + theme.bg + ring;
  const iconBoxClass = 'p-1.5 rounded-md border ' + theme.bg + ' ' + theme.border;
  const badgeClass = 'text-[10px] px-1.5 py-0 border ' + theme.bg + ' ' + theme.text + ' ' + theme.border;
  const nameClass = 'text-sm font-semibold truncate ' + theme.text;

  return (
    <div className="relative" data-testid={'org-node-' + node.id}>
      <div className={rowClass}>
        {hasKids ? (
          <button
            onClick={() => setOpen(!open)}
            className="mt-3 text-violet-700 hover:text-violet-900 transition-colors"
            aria-label={open ? 'collapse' : 'expand'}
            data-testid={'org-toggle-' + node.id}
          >
            {open ? <CaretDown size={16} weight="bold" /> : <CaretRight size={16} weight="bold" />}
          </button>
        ) : (
          <div className="w-4" />
        )}

        <div className={cardClass}>
          <div className="flex items-start justify-between gap-2">
            <div className="flex items-start gap-2 min-w-0 flex-1">
              <div className={iconBoxClass}>
                <Icon size={18} weight="duotone" className={theme.text} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={nameClass}>
                    {isAr ? node.name : node.name_en || node.name}
                  </span>
                  {node.role ? (
                    <Badge className={badgeClass}>
                      {isAr ? theme.ar : theme.en}
                    </Badge>
                  ) : null}
                </div>
                <div className="text-[11px] text-violet-700/70 mt-0.5 truncate">
                  {isAr ? (node.position || '-') : (node.position_en || node.position || '-')}
                  {node.department ? (
                    <span className="text-violet-700/50"> · {node.department}</span>
                  ) : null}
                </div>
                {node.email ? (
                  <div className="text-[10px] text-violet-700/50 mt-0.5 truncate">{node.email}</div>
                ) : null}
              </div>
            </div>
            {node.subs_count > 0 ? (
              <div className="flex items-center gap-1 text-[10px] text-violet-700/70 shrink-0 mt-0.5">
                <UsersThree size={12} weight="duotone" />
                <span>{node.subs_count}</span>
              </div>
            ) : null}
          </div>
        </div>
      </div>

      {hasKids && open ? (
        <div className={childWrap}>
          {children.map((child) =>
            React.createElement(TreeNode, {
              key: child.id,
              node: child,
              depth: depth + 1,
              isAr: isAr,
              search: search,
            })
          )}
        </div>
      ) : null}
    </div>
  );
}

function StatCard(props) {
  return (
    <div className="bg-white rounded-lg border border-violet-200 px-4 py-2 text-center min-w-[80px]">
      <div className="text-2xl font-bold text-violet-700">{props.value}</div>
      <div className="text-[10px] text-violet-700/70">{props.label}</div>
    </div>
  );
}

function LegendItem(props) {
  const Icon = props.theme.icon;
  const boxClass = 'p-1 rounded border ' + props.theme.bg + ' ' + props.theme.border;
  return (
    <div className="flex items-center gap-1.5">
      <div className={boxClass}>
        <Icon size={12} weight="duotone" className={props.theme.text} />
      </div>
      <span className={props.theme.text}>{props.label}</span>
    </div>
  );
}

export default function OrgChart() {
  const navigate = useNavigate();
  const { isAdmin } = useAuth();
  const { language, direction } = useLanguage();
  const isAr = language === 'ar';

  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(API + '/org-chart', { withCredentials: true });
      setData(res.data);
    } catch (e) {
      const detail = e.response && e.response.data && e.response.data.detail;
      toast.error(detail || (isAr ? 'تعذر تحميل المخطط' : 'Load failed'));
    } finally {
      setLoading(false);
    }
  }, [isAr]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/home');
      return;
    }
    fetchData();
  }, [isAdmin, navigate, fetchData]);

  const q = useMemo(() => search.trim().toLowerCase(), [search]);
  const searchIconPos = isAr ? 'right-3' : 'left-3';
  const searchIconClass = 'absolute top-2.5 text-violet-700/60 ' + searchIconPos;
  const inputPad = isAr ? 'pe-9' : 'ps-9';
  const inputClass = 'bg-white border-violet-200 ' + inputPad;

  return (
    <div className="min-h-screen bg-violet-50" dir={direction} data-testid="org-chart-page">
      <header className="sticky top-0 z-40 bg-white/95 backdrop-blur-sm shadow-sm border-b border-violet-200 py-4">
        <div className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <img src={LOGO_URL} alt="Lucent" className="h-10 object-contain" />
          <BackButton className="text-violet-700 hover:bg-violet-100" />
        </div>
      </header>

      <main className="max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8 py-8 space-y-6">
        <div className="flex items-end justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-3xl font-semibold text-violet-700 flex items-center gap-3">
              <TreeStructure size={32} weight="duotone" />
              {isAr ? 'المخطط التنظيمي' : 'Organisation Chart'}
            </h1>
            <p className="text-sm text-violet-700/70 mt-1">
              {isAr ? 'هيكل الشركة الكامل من المدير العام حتى آخر موظف' : 'Full company hierarchy from GM down to every employee'}
            </p>
          </div>
          {data ? (
            <div className="flex gap-3">
              <StatCard label={isAr ? 'الموظفون' : 'Employees'} value={data.stats.total} />
              <StatCard label={isAr ? 'المديرون' : 'Managers'} value={data.stats.managers} />
              <StatCard label={isAr ? 'الأقسام' : 'Departments'} value={data.stats.departments} />
            </div>
          ) : null}
        </div>

        <div className="relative max-w-md">
          <MagnifyingGlass size={16} className={searchIconClass} />
          <Input
            type="search"
            placeholder={isAr ? 'بحث بالاسم، القسم، أو المنصب…' : 'Search by name, department, position…'}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className={inputClass}
            data-testid="org-search"
          />
        </div>

        <Card className="border-violet-200">
          <CardContent className="p-6">
            {loading ? (
              <div className="text-center py-12 text-violet-700/60 text-sm">{isAr ? 'جاري التحميل…' : 'Loading…'}</div>
            ) : !data || data.roots.length === 0 ? (
              <div className="text-center py-12 text-violet-700/60 text-sm">
                {isAr ? 'لا يوجد موظفون لعرضهم' : 'No employees to show'}
              </div>
            ) : (
              <div className="space-y-3">
                {data.roots.map((root) => (
                  <TreeNode key={root.id} node={root} depth={0} isAr={isAr} search={q} />
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="text-xs text-violet-700/70 leading-relaxed">
          <p className="font-semibold mb-1">{isAr ? 'دلالة الألوان:' : 'Color legend:'}</p>
          <div className="flex gap-3 flex-wrap">
            <LegendItem theme={ROLE_THEME.gm} label={isAr ? 'المدير العام' : 'General Manager'} />
            <LegendItem theme={ROLE_THEME.dept_manager} label={isAr ? 'مدير قسم' : 'Department Manager'} />
            <LegendItem theme={ROLE_THEME.finance} label={isAr ? 'مدير مالي' : 'Finance Manager'} />
            <LegendItem theme={ROLE_THEME.employee} label={isAr ? 'موظف' : 'Employee'} />
          </div>
        </div>
      </main>
    </div>
  );
}
